// Leaderboard data fetchers.
//
// Two boards live here today: the daily-cities flagship (date-scoped,
// score-driven) and the per-quiz boards (all-time, correct-then-ms
// driven). They share the scope axis (global / following) but
// diverge on row shape and ranking signal — distance vs. correctness
// are not the same thing — so they get separate types and fetchers
// rather than a forced common shape.
//
// Date semantics for daily: the daily mode picks its puzzle seed +
// run_key from the *player's local date* (so a player in CST and a
// player in JST who both play "Sunday" land on the same puzzle and
// the same leaderboard). The leaderboard endpoint must therefore
// query by the same local date — a UTC default would silently
// exclude any player whose local "today" doesn't match UTC's current
// date (e.g. CST evenings when UTC has already rolled to tomorrow).

import { api } from './api';
import { localDateString } from '@atlasguess/games';
import type { DonorTier } from './stores/auth';

export interface LeaderboardRow {
  rank: number;
  username: string;
  donor_tier: DonorTier;
  /** Per-round scores (0..100 each), in play order. Drives the
   *  emoji + score-cell ribbon column on the table. */
  scores: number[];
  /** Sum of per-round scores (0..500 for a 5-round daily). Primary ranking signal. */
  total_score: number;
  /** Sum of per-round km. Tiebreaker for score-500 ties. */
  total_km: number;
  /** Average km per round — drives the "Avg" column directly. */
  avg_km: number;
}

export interface UserRank {
  rank: number;
  total_score: number;
  total_km: number;
}

export type LeaderboardScope = 'global' | 'following';

export interface DailyLeaderboard {
  mode_id: string;
  date: string;
  scope: LeaderboardScope;
  rows: LeaderboardRow[];
  count: number;
  user_rank: UserRank | null;
}

/** One row of a quiz leaderboard. correct/total are first-try counts;
 *  ms is total wall-clock for the run. Ranking: correct DESC, ms ASC.
 *  Mirrors the per-user "best run" surfaced on the stats card so the
 *  numbers a player sees on their profile match their leaderboard cell. */
export interface QuizLeaderboardRow {
  rank: number;
  username: string;
  donor_tier: DonorTier;
  correct: number;
  total: number;
  ms: number;
  /** Local date string (YYYY-MM-DD) of the run that produced this
   *  best result. Drives the "last played" column. */
  last_played: string;
}

export interface QuizUserRank {
  rank: number;
  correct: number;
  total: number;
  ms: number;
}

export interface QuizLeaderboard {
  mode_id: string;
  scope: LeaderboardScope;
  rows: QuizLeaderboardRow[];
  count: number;
  user_rank: QuizUserRank | null;
}

export async function fetchDailyLeaderboard(
  date?: string,
  scope: LeaderboardScope = 'global',
): Promise<DailyLeaderboard> {
  // Default to the player's local date so the board matches the
  // puzzle they actually just played. Caller can pass an explicit
  // date for past-day views.
  const effectiveDate = date ?? localDateString();
  const params = new URLSearchParams({ date: effectiveDate });
  if (scope !== 'global') params.set('scope', scope);
  return api<DailyLeaderboard>(`/api/leaderboards/daily?${params.toString()}`);
}

/**
 * Fetch the all-time top board for a single quiz mode. Mode id is
 * the registered quiz id (`countries-europe-quiz`, etc.). Unknown
 * mode ids return an empty row set rather than 404 — keeps the SPA's
 * mode-tab strip simple even for never-played quizzes.
 */
export async function fetchQuizLeaderboard(
  modeId: string,
  scope: LeaderboardScope = 'global',
): Promise<QuizLeaderboard> {
  const params = new URLSearchParams({ mode_id: modeId });
  if (scope !== 'global') params.set('scope', scope);
  return api<QuizLeaderboard>(`/api/leaderboards/quiz?${params.toString()}`);
}
